/**
 * Tour types and the two rules the frame needs before the catalog is loaded: which steps a profile follows and
 * whether an address shows a step's screen. Kept apart from catalog.ts so the catalog text stays out of the first
 * screen's bundle (it loads with the first tour or with the help center).
 */

export type RoleCode = 'OPERADOR' | 'CONFERENTE' | 'FINANCEIRO' | 'GESTOR' | 'ADMIN';

export interface TourStep {
  /** Screen of the step. Absent: the step belongs to the frame (menu, header) and runs on whatever screen is open. */
  route?: string;
  /** `data-tour` values, in order of preference. */
  targets: readonly string[];
  title: string;
  body: string;
  /** Shown only when the unit grants any of these permissions (the same rule as the menu item of the screen). */
  anyPerm?: readonly string[];
}

export interface Tour {
  id: string;
  title: string;
  summary: string;
  kind: 'geral' | 'perfil' | 'fluxo';
  /** Profiles for which the tour is recommended in the help center. Any profile may still start it. */
  roles?: readonly RoleCode[];
  steps: readonly TourStep[];
}

/**
 * Whether `pathname` shows the screen of `route`: the address itself, or the address plus one parameter segment
 * with a digit (`/movimento/2026-10-18`). `/conciliacao/assistida` is another screen, not `/conciliacao`.
 */
export function onRoute(pathname: string, route: string): boolean {
  if (pathname === route) return true;
  if (route === '/' || !pathname.startsWith(`${route}/`)) return false;
  const rest = pathname.slice(route.length + 1);
  return !rest.includes('/') && /\d/.test(rest);
}

/**
 * Steps a profile can follow: those without a permission and those whose screen the unit opens. `systemPerms` holds
 * what any of the person's units grants (system health is checked in any unit by the server).
 */
export function stepsFor(tour: Tour, perms: readonly string[], systemPerms: readonly string[] = perms) {
  const has = (p: string) => perms.includes(p) || (p.startsWith('system.') && systemPerms.includes(p));
  return tour.steps.filter((s) => !s.anyPerm || s.anyPerm.some(has));
}
